import React, { useState, useEffect, useCallback, useRef } from "react";
import semesterNoteService from "../../services/semesterNoteService";
import semesterService from "../../services/semesterService";
import {
  resolveCurrentSemesterId,
  extractSemesterNumber,
} from "../../utils/dateUtils";

function SemesterNotesModal({ isOpen, onClose, student }) {
  const [semesters, setSemesters] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newText, setNewText] = useState("");
  const [newDate, setNewDate] = useState(todayIso());
  const [loadingSemesters, setLoadingSemesters] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const initializedForStudentId = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      initializedForStudentId.current = null;
      return;
    }
    if (!student) return;
    if (initializedForStudentId.current !== student.id) {
      initializedForStudentId.current = null;
    }
    loadSemesters();
    setTimeout(() => textareaRef.current?.focus(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, student?.id]);

  const loadSemesters = async () => {
    setLoadingSemesters(true);
    setError(null);
    try {
      const primaryClass = student.classes?.[0];
      const schoolId = primaryClass?.schoolId ?? student.schoolId;
      const classId = primaryClass?.id ?? student.classId;

      if (!schoolId || !classId) {
        setSemesters([]);
        return;
      }

      let data = await semesterService.getSemestersByClass(schoolId, classId);
      if (!data || data.length === 0) {
        data = await semesterService.getSemestersBySchool(schoolId);
      }

      const sorted = (data || []).sort(
        (a, b) =>
          (extractSemesterNumber(a) ?? 999) -
          (extractSemesterNumber(b) ?? 999),
      );
      setSemesters(sorted);
      initializedForStudentId.current = student.id;
    } catch (err) {
      console.error("Failed to load semesters", err);
      setError("Could not load semesters.");
    } finally {
      setLoadingSemesters(false);
    }
  };

  const loadNotes = useCallback(async () => {
    if (!student?.id) return;
    setLoadingNotes(true);
    setError(null);
    try {
      const data = await semesterNoteService.getNotes(student.id);
      setNotes(data || []);
    } catch (err) {
      console.error("Failed to load notes", err);
      setError("Could not load notes.");
    } finally {
      setLoadingNotes(false);
    }
  }, [student?.id]);

  useEffect(() => {
    if (isOpen) loadNotes();
  }, [isOpen, loadNotes]);

  const handleSave = async () => {
    if (!newText.trim()) return;

    const semesterId = resolveCurrentSemesterId({
      learner: student,
      semesters,
      referenceDate: new Date(newDate),
    });

    if (!semesterId) {
      setError("Could not determine semester for this date.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await semesterNoteService.createNote({
        learnerId: student.id,
        semesterId,
        noteDate: newDate,
        text: newText.trim(),
      });
      setNotes((prev) => [created, ...prev]);
      setNewText("");
      setNewDate(todayIso());
      textareaRef.current?.focus();
    } catch (err) {
      console.error("Failed to save note", err);
      setError(err?.response?.data || "Could not save note.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    setDeletingId(noteId);
    setError(null);
    try {
      await semesterNoteService.deleteNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      console.error("Failed to delete note", err);
      setError("Could not delete note.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  const learnerName = student
    ? `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
      student.username
    : "";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-brand-xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden border border-pale-dawn">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-ice-white to-pale-dawn/30 border-b border-pale-dawn">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
                <i className="fas fa-note-sticky text-sm"></i>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-grey-dusk leading-tight">
                  Quick Notes
                </h2>
                <p className="text-sm text-slate-cloud truncate">
                  {learnerName}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg text-gray-400 hover:text-grey-dusk hover:bg-white transition-colors flex items-center justify-center"
              aria-label="Close"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2">
            <i className="fas fa-exclamation-triangle mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        {/* New note input */}
        <div className="p-5 border-b border-pale-dawn bg-white">
          <div className="rounded-2xl border border-pale-dawn bg-ice-white/60 p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="sm:w-40">
                <label className="block text-xs font-semibold text-grey-dusk mb-1.5">
                  Date
                </label>
                <input
                  type="date"
                  className="input-brand text-sm w-full bg-white"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="flex-1">
                <label className="block text-xs font-semibold text-grey-dusk mb-1.5">
                  Note
                </label>
                <textarea
                  ref={textareaRef}
                  className="input-brand text-sm w-full resize-none bg-white min-h-[76px]"
                  rows={3}
                  placeholder="Write a short note..."
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  disabled={saving}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      handleSave();
                    }
                  }}
                />
              </div>
            </div>

            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-gray-500">
                <i className="fas fa-keyboard mr-1 text-gray-400"></i>
                Ctrl+Enter to save
              </p>

              <button
                onClick={handleSave}
                disabled={saving || !newText.trim() || loadingSemesters}
                className="h-9 px-4 bg-sunrise-red text-white rounded-lg text-sm font-semibold hover:bg-sunrise-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {saving ? (
                  <i className="fas fa-spinner fa-spin text-xs"></i>
                ) : (
                  <i className="fas fa-plus text-xs"></i>
                )}
                Save note
              </button>
            </div>
          </div>
        </div>

        {/* Notes list */}
        <div className="px-5 py-4 overflow-y-auto flex-1 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-grey-dusk flex items-center">
              <i className="fas fa-list-ul text-sunrise-red mr-2"></i>
              Notes
            </h3>
            <span className="text-xs text-slate-cloud bg-ice-white border border-pale-dawn rounded-full px-2.5 py-1">
              {notes.length} total
            </span>
          </div>

          {loadingNotes || loadingSemesters ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="h-20 bg-ice-white border border-pale-dawn rounded-xl animate-pulse"
                />
              ))}
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 bg-ice-white border border-pale-dawn rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-note-sticky text-gray-400 text-lg"></i>
              </div>
              <h4 className="font-semibold text-grey-dusk mb-1">
                No notes yet
              </h4>
              <p className="text-sm text-slate-cloud">
                Add the first quick note for this learner.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onDelete={handleDelete}
                  deletingId={deletingId}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NoteCard({ note, onDelete, deletingId }) {
  const dateStr = note.noteDate
    ? new Date(note.noteDate).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

  return (
    <div className="group bg-white border border-pale-dawn rounded-xl px-4 py-3 hover:shadow-sm hover:border-gray-300 transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center text-xs font-semibold text-sunrise-red bg-sunrise-red/10 px-2 py-1 rounded-full">
              <i className="fas fa-calendar-day mr-1.5"></i>
              {dateStr}
            </span>

            {note.semesterName && (
              <span className="inline-flex items-center text-xs text-grey-dusk bg-ice-white border border-pale-dawn px-2 py-1 rounded-full">
                {note.semesterName}
              </span>
            )}

            {note.authorName && (
              <span className="text-xs text-slate-cloud">
                by {note.authorName}
              </span>
            )}
          </div>

          <p className="text-sm text-grey-dusk leading-relaxed whitespace-pre-wrap break-words">
            {note.text}
          </p>
        </div>

        <button
          onClick={() => onDelete(note.id)}
          disabled={deletingId === note.id}
          className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-50"
          title="Delete note"
          aria-label="Delete note"
        >
          {deletingId === note.id ? (
            <i className="fas fa-spinner fa-spin text-xs"></i>
          ) : (
            <i className="fas fa-trash text-xs"></i>
          )}
        </button>
      </div>
    </div>
  );
}

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

export default SemesterNotesModal;